import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Card, Spinner } from "@aigntx/ui";
import { providerInfo } from "@aigntx/ai-gateway";
import { endpoints } from "../api";
import { useTenant } from "../tenant";
import { useAuth } from "../auth";
import type { MyAction, MyUsage } from "../types";

export function DashboardPage() {
  const tenant = useTenant();
  const { user } = useAuth();
  const { t } = useTranslation();
  const ai = providerInfo(tenant.ai.provider);
  const [usage, setUsage] = useState<MyUsage | null>(null);
  const [usageFailed, setUsageFailed] = useState(false);

  useEffect(() => {
    endpoints.getMyUsage().then(setUsage).catch(() => setUsageFailed(true));
  }, []);

  const pct =
    usage && usage.tokensQuota > 0
      ? Math.min(100, Math.round((usage.tokensToday / usage.tokensQuota) * 100))
      : 0;

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t("dashboard.welcome", { name: user?.name ?? "" })}</h1>
        <p className="page__sub">{t("dashboard.intro")}</p>
      </header>

      {tenant.modules.includes("eng.projects") && <MyActionsCard />}

      <HubTiles />

      <div className="grid">
        <Card title={t("dashboard.type")}>
          <div className="stat">{tenant.type.replace(/_/g, " ")}</div>
        </Card>

        <Card title={t("dashboard.ai")}>
          <div className="kv">
            <span className="kv__k">{t("dashboard.provider")}</span>
            <span className="kv__v">{ai?.label ?? tenant.ai.provider}</span>
          </div>
          <div className="kv">
            <span className="kv__k">{t("dashboard.model")}</span>
            <span className="kv__v mono">{tenant.ai.model}</span>
          </div>
        </Card>

        <Card title={t("dashboard.usage")}>
          {usage ? (
            <>
              <div className="kv">
                <span className="kv__k">{t("dashboard.tokensToday")}</span>
                <span className="kv__v mono">
                  {usage.tokensToday.toLocaleString()}
                  {usage.tokensQuota > 0 ? ` / ${usage.tokensQuota.toLocaleString()}` : ""}
                </span>
              </div>
              {usage.tokensQuota > 0 ? (
                <div className="bar" style={{ marginTop: "var(--space-2)" }}>
                  <span className={pct > 90 ? "bar__fill is-hot" : "bar__fill"} style={{ width: `${pct}%` }} />
                </div>
              ) : (
                <div className="muted">{t("dashboard.unlimited")}</div>
              )}
            </>
          ) : usageFailed ? (
            <div className="muted">{t("common.loadFailed")}</div>
          ) : (
            <div className="center">
              <Spinner />
            </div>
          )}
        </Card>

        <Card title={t("dashboard.limits")}>
          <div className="kv">
            <span className="kv__k">{t("dashboard.seats")}</span>
            <span className="kv__v">{tenant.limits.seats}</span>
          </div>
          <div className="kv">
            <span className="kv__k">{t("dashboard.storage")}</span>
            <span className="kv__v">{tenant.limits.storageGb} GB</span>
          </div>
        </Card>

        <Card title={t("dashboard.modules")} className="grid__wide">
          <div className="chips">
            {tenant.modules.map((m) => (
              <Badge key={m} tone={m.startsWith("core.") ? "good" : "accent"}>
                {m}
              </Badge>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}


/**
 * The hub every signed-in person lands on: only the areas this user's role,
 * permissions and their company's enabled modules actually allow. A contractor
 * sees their forms; a tenant admin also sees the company and its team.
 */
function HubTiles() {
  const tenant = useTenant();
  const { can, user } = useAuth();
  const { t } = useTranslation();

  const has = (m: string) => tenant.modules.includes(m);
  const tiles = [
    {
      to: "/projects",
      key: "projects",
      show: has("eng.projects") && (can("projects.read") || can("projects.manage")),
    },
    { to: "/records", key: "records", show: has("crm") },
    { to: "/data", key: "data", show: true },
    {
      to: "/company",
      key: "company",
      show: can("users.read") || can("tenant.manage"),
    },
    { to: "/settings", key: "settings", show: true },
    { to: "/admin", key: "admin", show: Boolean(user?.isPlatformAdmin) },
    { to: "/canvas", key: "canvas", show: Boolean(user?.isPlatformAdmin) },
  ].filter((x) => x.show);

  return (
    <section className="hub">
      <h2 className="hub__title">{t("hub.title")}</h2>
      <p className="page__sub hub__sub">{t("hub.hint")}</p>
      <div className="hub__grid">
        {tiles.map((x) => (
          <Link key={x.key} to={x.to} className="hubtile">
            <span className="hubtile__name">{t(`hub.tile.${x.key}`)}</span>
            <span className="hubtile__hint">{t(`hub.tileHint.${x.key}`)}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

/**
 * "What is waiting for me" across every project — the first thing each party
 * should see. Overdue and urgent items sort to the top server-side.
 */
function MyActionsCard() {
  const { t } = useTranslation();
  const [items, setItems] = useState<MyAction[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    endpoints.myActions().then(setItems).catch(() => setFailed(true));
  }, []);

  // Show the card WHILE it loads. Returning null made it appear from nowhere a
  // moment after the page settled, which reads as the page still being broken.
  if (failed) {
    return (
      <Card title={t("inbox.title")} className="inbox-card">
        <p className="muted">{t("common.loadFailed")}</p>
      </Card>
    );
  }
  if (!items) {
    return (
      <Card title={t("inbox.title")} className="inbox-card">
        <div className="center">
          <Spinner />
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={t("inbox.title")}
      subtitle={items.length ? t("inbox.count", { count: items.length }) : t("inbox.clear")}
      className="inbox-card"
    >
      {items.length === 0 ? (
        <p className="muted">{t("inbox.empty")}</p>
      ) : (
        <ul className="inbox">
          {items.map((i) => (
            <li key={i.formId} className={i.overdue ? "inbox__item is-late" : "inbox__item"}>
              <Link className="inbox__link" to={`/projects/${i.projectId}/forms/${i.formId}`}>
                <span className="inbox__no mono">{i.number}</span>
                <span className="inbox__main">
                  <span className="inbox__subject">{i.subject || i.title}</span>
                  <span className="inbox__step">{i.stepLabel}</span>
                </span>
                <span className="inbox__meta">
                  <span className="muted">{i.projectName}</span>
                  {i.urgent && <Badge tone="danger">{t("inbox.urgent")}</Badge>}
                  {i.overdue ? (
                    <Badge tone="danger">{t("forms.overdue")}</Badge>
                  ) : i.dueAt ? (
                    <span className="muted mono">{new Date(i.dueAt).toLocaleDateString()}</span>
                  ) : null}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
